/** Requerimientos: lista con buscador, panel de filtros y tarjetas legibles. */
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { MobileLayout } from '@/components/MobileLayout';
import { MobileButton } from '@/components/MobileButton';
import { EntityListCard } from '@/components/EntityListCard';
import { MobileFilterSheet } from '@/components/MobileFilterSheet';
import { MobileIcon } from '@/components/MobileIcon';
import { MobileSelect } from '@/components/MobileSelect';
import { SearchInput } from '@/components/SearchInput';
import { CardListSkeleton } from '@/components/LoadingSkeleton';
import { EmptyState } from '@/components/EmptyState';
import { PullIndicator } from '@/components/PullIndicator';
import { usePullToRefresh } from '@/hooks/usePullToRefresh';
import {
  useMobileRequirementsQuery,
  type RequirementFilters,
} from '@/hooks/useMobileRequirements';
import { useMobileAuth } from '@/state/MobileAuthContext';

const STATUS_OPTIONS = [
  { value: 'all', label: 'Todos los estados' },
  { value: 'draft', label: 'Borrador' },
  { value: 'technical_review', label: 'Revisión técnica' },
  { value: 'observed', label: 'Observado' },
  { value: 'in_design', label: 'En diseño' },
  { value: 'design_approved', label: 'Diseño aprobado' },
  { value: 'on_budget', label: 'En presupuesto' },
  { value: 'completed', label: 'Completado' },
  { value: 'cancelled', label: 'Cancelado' },
];

const TYPE_OPTIONS = [
  { value: 'all', label: 'Todos los tipos' },
  { value: 'standard', label: 'Estándar' },
  { value: 'project', label: 'Proyecto' },
];

const SOURCE_OPTIONS: Array<{ value: RequirementFilters['source']; label: string }> = [
  { value: 'all', label: 'Todos' },
  { value: 'real', label: 'Reales' },
  { value: 'quote_generator', label: 'Del cotizador' },
];

function formatDate(value: string | undefined): string {
  if (!value) return '-';
  const date = new Date(value.length <= 10 ? `${value}T00:00:00` : value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export function RequirementsPage() {
  const { user, hasPermission } = useMobileAuth();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [appliedShowAll, setAppliedShowAll] = useState(false);
  const [appliedSource, setAppliedSource] = useState<RequirementFilters['source']>('all');
  const [appliedStatus, setAppliedStatus] = useState('all');
  const [appliedType, setAppliedType] = useState('all');
  const [liveShowAll, setLiveShowAll] = useState(false);
  const [liveSource, setLiveSource] = useState<RequirementFilters['source']>('all');
  const [liveStatus, setLiveStatus] = useState('all');
  const [liveType, setLiveType] = useState('all');
  const requirementsQuery = useMobileRequirementsQuery({
    source: appliedSource,
    status: appliedStatus,
    type: appliedType,
  });
  const { bindRef, pulling, refreshing } = usePullToRefresh({
    onRefresh: () => requirementsQuery.refetch(),
    disabled: user?.active_tenant_id == null || !hasPermission('customer_requirements.view'),
  });

  const filtered = useMemo(() => {
    const items = requirementsQuery.data?.items ?? [];
    const mine = appliedShowAll
      ? items
      : items.filter(
          (item) =>
            (item.commercial_owner_id && item.commercial_owner_id === user?.id) ||
            (item.created_by && item.created_by === user?.id),
        );
    if (!search.trim()) return mine;
    const q = search.trim().toLowerCase();
    return mine.filter(
      (item) =>
        item.code.toLowerCase().includes(q) ||
        item.name.toLowerCase().includes(q) ||
        (item.client?.legal_name ?? '').toLowerCase().includes(q),
    );
  }, [requirementsQuery.data, search, appliedShowAll, user?.id]);

  const activeFilterCount =
    (appliedShowAll ? 1 : 0) +
    (appliedSource !== 'all' ? 1 : 0) +
    (appliedStatus !== 'all' ? 1 : 0) +
    (appliedType !== 'all' ? 1 : 0);
  const hasFilters = activeFilterCount > 0 || search.trim() !== '';

  function openFilters() {
    setLiveShowAll(appliedShowAll);
    setLiveSource(appliedSource);
    setLiveStatus(appliedStatus);
    setLiveType(appliedType);
    setFilterOpen(true);
  }

  function applyFilters() {
    setAppliedShowAll(liveShowAll);
    setAppliedSource(liveSource);
    setAppliedStatus(liveStatus);
    setAppliedType(liveType);
    setFilterOpen(false);
  }

  function resetFilters() {
    setLiveShowAll(false);
    setLiveSource('all');
    setLiveStatus('all');
    setLiveType('all');
    setAppliedShowAll(false);
    setAppliedSource('all');
    setAppliedStatus('all');
    setAppliedType('all');
    setFilterOpen(false);
  }

  return (
    <MobileLayout
      title="Requerimientos"
      subtitle={`${filtered.length} de ${requirementsQuery.data?.total ?? 0} en total`}
    >
      <div ref={bindRef} className="space-y-4">
        <PullIndicator pulling={pulling} refreshing={refreshing} />
        {hasPermission('customer_requirements.create') && (
          <Link to="/m/requirements/new">
            <MobileButton
              variant="primary"
              size="md"
              fullWidth
              icon={<MobileIcon name="add" className="h-5 w-5" />}
            >
              Nuevo requerimiento
            </MobileButton>
          </Link>
        )}

        <div className="flex items-center gap-2">
          <div className="min-w-0 flex-1">
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder="Buscar por código, nombre o cliente..."
            />
          </div>
          <MobileButton
            variant="secondary"
            size="md"
            onClick={openFilters}
            icon={<MobileIcon name="filter_list" className="h-5 w-5" />}
            className="shrink-0 px-4"
          >
            {activeFilterCount > 0 ? `Filtros (${activeFilterCount})` : 'Filtros'}
          </MobileButton>
        </div>

        {requirementsQuery.isPending && <CardListSkeleton count={4} />}

        {requirementsQuery.isError && (
          <EmptyState
            icon="error"
            title="No se pudo cargar"
            description="Verifica tu conexión e intenta de nuevo."
            actionLabel="Reintentar"
            onAction={() => requirementsQuery.refetch()}
          />
        )}

        {!requirementsQuery.isPending && !requirementsQuery.isError && filtered.length === 0 && (
          <EmptyState
            icon={hasFilters ? 'search_off' : 'assignment'}
            title={hasFilters ? 'Sin resultados' : 'Sin requerimientos'}
            description={
              hasFilters
                ? 'Ninguno coincide con los filtros.'
                : appliedShowAll
                  ? 'Aún no hay requerimientos.'
                  : 'Aún no tienes requerimientos asignados.'
            }
          />
        )}

        <div className="space-y-3">
          {filtered.map((item) => (
            <EntityListCard
              key={item.id}
              eyebrow={item.code || item.name}
              title={item.name && item.name !== item.code ? item.name : undefined}
              status={item.status}
              tag={item.source === 'quote_generator' ? 'Cotizador' : undefined}
              ariaLabel={item.code || item.name}
              metadata={[
                ...(item.client?.legal_name
                  ? [{ icon: 'business' as const, text: item.client.legal_name }]
                  : []),
                ...(item.required_date
                  ? [{ icon: 'schedule' as const, text: `Requerido: ${formatDate(item.required_date)}` }]
                  : []),
              ]}
              onClick={() => navigate(`/m/requirements/${item.id}`)}
            />
          ))}
        </div>
      </div>

      <MobileFilterSheet
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        onApply={applyFilters}
        onReset={resetFilters}
      >
        <div>
          <p className="mb-1.5 text-sm font-medium text-text-secondary">Autor</p>
          <div
            role="radiogroup"
            aria-label="Filtrar requerimientos por autor"
            className="flex items-center gap-1 rounded-full bg-bg-tertiary p-1"
          >
            {(
              [
                { value: false, label: 'Solo míos' },
                { value: true, label: 'Todos' },
              ] as const
            ).map((option) => (
              <button
                key={option.label}
                type="button"
                role="radio"
                aria-checked={liveShowAll === option.value}
                onClick={() => setLiveShowAll(option.value)}
                className={clsx(
                  'flex h-11 min-h-[44px] flex-1 items-center justify-center rounded-full text-sm font-semibold transition-all active:scale-[0.97]',
                  liveShowAll === option.value
                    ? 'bg-white text-text-primary shadow-card'
                    : 'text-text-secondary hover:text-text-primary',
                )}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <p className="mb-1.5 text-sm font-medium text-text-secondary">Origen</p>
          <div
            role="radiogroup"
            aria-label="Filtrar requerimientos por origen"
            className="flex items-center gap-1 rounded-full bg-bg-tertiary p-1"
          >
            {SOURCE_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                role="radio"
                aria-checked={liveSource === option.value}
                onClick={() => setLiveSource(option.value)}
                className={clsx(
                  'flex h-11 min-h-[44px] flex-1 items-center justify-center rounded-full px-2 text-sm font-semibold transition-all active:scale-[0.97]',
                  liveSource === option.value
                    ? 'bg-white text-text-primary shadow-card'
                    : 'text-text-secondary hover:text-text-primary',
                )}
              >
                <span className="truncate">{option.label}</span>
              </button>
            ))}
          </div>
        </div>

        <MobileSelect
          label="Estado"
          staticLabel
          value={liveStatus}
          options={STATUS_OPTIONS}
          onChange={(event) => setLiveStatus(event.target.value)}
        />

        <MobileSelect
          label="Tipo"
          staticLabel
          value={liveType}
          options={TYPE_OPTIONS}
          onChange={(event) => setLiveType(event.target.value)}
        />
      </MobileFilterSheet>
    </MobileLayout>
  );
}
